import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useTheme } from "../lib/theme";
import { onRippleClick } from "../lib/interactions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";
import CartPopover from "./CartPopover";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <header className={`site-header ${scrolled ? "is-scrolled" : ""}`} style={{ borderBottom: "1px solid var(--border)", position: "sticky", top: 0, zIndex: 50 }}>
        <div className="container" style={{ display: "flex", alignItems: "center", height: "var(--header-h)", gap: 24 }}>
          <Link to="/" aria-label="전기닷컴 홈" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div aria-hidden="true" style={{ width: 32, height: 32, background: "var(--primary)", borderRadius: 6, display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontFamily: "var(--font-display)" }}>
              電
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>전기닷컴</div>
              <div style={{ fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.15em", fontFamily: "var(--font-display)", fontWeight: 600 }}>B2B MALL</div>
            </div>
          </Link>

          {user && user.status === "approved" && (
            <nav aria-label="주 내비게이션" className="site-nav-desktop" style={{ display: "flex", gap: 4, marginLeft: 20 }}>
              {user.role === "admin" ? (
                <NavLink to="/admin" className="btn btn-ghost btn-sm">관리자</NavLink>
              ) : (
                <>
                  <NavLink to="/catalog" className="btn btn-ghost btn-sm">카탈로그</NavLink>
                  <NavLink to="/orders" className="btn btn-ghost btn-sm">주문 내역</NavLink>
                </>
              )}
            </nav>
          )}

          <div className="grow" />

          <button className="btn btn-ghost btn-sm" onClick={toggle} aria-label={theme === "dark" ? "라이트 모드" : "다크 모드"} title={theme === "dark" ? "라이트 모드" : "다크 모드"}>
            {theme === "dark" ? "☀" : "◐"}
          </button>

          <div className="header-actions-desktop" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {user ? (
              <>
                {user.role !== "admin" && (
                  <CartPopover>
                    <Link to="/cart" className="btn btn-outline btn-sm" aria-label={`장바구니, ${count}개 품목`}>
                      <FontAwesomeIcon icon={faCartShopping} style={{ width: 13, height: 13 }} />
                      {count > 0 && <span className="badge" style={{ marginLeft: 4 }}>{count}</span>}
                    </Link>
                  </CartPopover>
                )}
                <Link to="/account" className="btn btn-ghost btn-sm" title="내 계정">{user.company_name}</Link>
                <button className="btn btn-ghost btn-sm" onClick={async () => { await logout(); nav("/"); }}>로그아웃</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline btn-sm">로그인</Link>
                <Link to="/signup" className="btn btn-primary btn-sm" onMouseDown={onRippleClick}>회원가입</Link>
              </>
            )}
          </div>

          <button className="btn btn-ghost btn-sm site-nav-toggle" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "메뉴 닫기" : "메뉴 열기"} style={{ display: "none" }}>
            {open ? "✕" : "☰"}
          </button>
        </div>
      </header>

      {/* Mobile fullscreen overlay */}
      <div role="dialog" aria-modal="true" aria-label="모바일 메뉴" aria-hidden={!open}
        className={`site-menu-overlay ${open ? "is-open" : ""}`}>
        <nav style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "center" }}>
          {user && user.status === "approved" && (
            <>
              {user.role === "admin" ? (
                <MobileLink to="/admin" onClick={close}>관리자</MobileLink>
              ) : (
                <>
                  <MobileLink to="/catalog" onClick={close}>카탈로그</MobileLink>
                  <MobileLink to="/orders" onClick={close}>주문 내역</MobileLink>
                  <MobileLink to="/cart" onClick={close}>장바구니 {count > 0 && <span className="badge">{count}</span>}</MobileLink>
                </>
              )}
            </>
          )}
          {user ? (
            <>
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>{user.company_name}</div>
              <button className="btn btn-outline btn-lg" onClick={async () => { await logout(); close(); nav("/"); }}>로그아웃</button>
            </>
          ) : (
            <>
              <MobileLink to="/login" onClick={close}>로그인</MobileLink>
              <Link to="/signup" onClick={close} className="btn btn-cta btn-lg" onMouseDown={onRippleClick}>사업자 회원가입</Link>
            </>
          )}
        </nav>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .site-nav-desktop, .header-actions-desktop { display: none !important; }
          .site-nav-toggle { display: inline-flex !important; }
        }
        .site-menu-overlay {
          position: fixed;
          inset: var(--header-h) 0 0 0;
          z-index: 49;
          background: color-mix(in oklch, var(--bg) 96%, transparent);
          backdrop-filter: saturate(180%) blur(16px);
          -webkit-backdrop-filter: saturate(180%) blur(16px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--s-8);
          opacity: 0;
          pointer-events: none;
          transition: opacity var(--dur-base) var(--ease-out);
        }
        .site-menu-overlay.is-open {
          opacity: 1;
          pointer-events: auto;
        }
      `}</style>
    </>
  );
}

function MobileLink({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <NavLink to={to} onClick={onClick}
      className={({ isActive }) => isActive ? "active-link" : ""}
      style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.02em" }}>
      {children}
    </NavLink>
  );
}
